import Decimal from 'decimal.js';

import { ItemConfiguration } from 'apis/picker-api/types';
import { NutritionSetting, Option, OptionBinding, Translation } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { VatFieldValue, initialVatFieldValue } from 'modules/menu/components/VatCategories';
import getCurrencyMaxDecimals from 'modules/menu/components/getCurrencyMaxDecimals';

import { FormValues as ItemDetailsFormValues } from './ItemDetailsForm/types';

export const getDiscountPercentage = (discountedPrice: string, originalPrice: string) => {
  const discountedPriceDecimal = new Decimal(discountedPrice);
  const originalPriceDecimal = new Decimal(originalPrice);
  const division = discountedPriceDecimal.div(originalPriceDecimal);

  return new Decimal(1.0).sub(division).times(100).round().toNumber();
};

export const getPercentageDiscountPrice = (basePrice: number, percentage: number, currency: Currency) => {
  const maxDecimals = getCurrencyMaxDecimals(currency);

  const basePriceDecimal = new Decimal(basePrice);
  const discountPercentageDecimal = new Decimal(percentage).div(100);

  const priceDiscountDecimal = basePriceDecimal.times(discountPercentageDecimal);
  const discountedPriceDecimal = basePriceDecimal.sub(priceDiscountDecimal);

  return discountedPriceDecimal.toFixed(maxDecimals).toString();
};

export const updatePrimaryLanguageTranslation = (
  primaryLanguage: Language,
  newValue: string,
  translations: Translation[],
): Translation[] => {
  const oldPrimaryLanguageTranslation = translations.find(translation => translation.lang === primaryLanguage);
  const newPrimaryLanguageTranslation = { lang: primaryLanguage, value: newValue, verified: true };

  if (!oldPrimaryLanguageTranslation) {
    return [newPrimaryLanguageTranslation, ...translations.map(translation => ({ ...translation, verified: false }))];
  }

  const primaryLanguageTranslationHasChanged = oldPrimaryLanguageTranslation.value !== newValue;

  return translations.map(translation =>
    translation.lang === primaryLanguage
      ? newPrimaryLanguageTranslation
      : { ...translation, verified: primaryLanguageTranslationHasChanged ? false : translation.verified },
  );
};

export const buildNewItemFormValues = (
  categoryId: string | null,
  pickingAreaId?: string,
  vat: VatFieldValue = initialVatFieldValue(undefined, undefined),
  nutritionSettings: NutritionSetting[] = [],
  itemConfig: ItemConfiguration = { itemId: '', menuId: '', storageLocation: '' },
): ItemDetailsFormValues => {
  return {
    additives: '',
    alcoholPercentage: 0,
    allergens: '',
    availabilityPeriod: { start: null, end: null },
    barcodeGtin: '',
    brandId: '',
    bundleOffer: false,
    caffeineContentUnits: undefined,
    caffeineContentValue: undefined,
    categoryId,
    countryOfOrigin: '',
    depositOption: '',
    description: '',
    dietaryPreferences: [],
    disableBikeDelivery: false,
    disableCarDelivery: false,
    disableMotorcycleDelivery: false,
    fixedDiscountPrice: undefined,
    discountPercentage: undefined,
    discountType: undefined,
    distributorInfo: '',
    enabled: true,
    enableDelivery: true,
    enableEatin: true,
    enableTakeaway: true,
    isRestrictedItem: false,
    isOTCRestricted: false,
    isAgeRestricted: false,
    ageLimit: undefined,
    isDeliveryHandshakeRestricted: false,
    image: [],
    ingredients: '',
    merchantSku: '',
    inventoryMode: 'binary',
    inStock: true,
    mandatoryWarnings: [],
    moreInformation: '',
    name: '',
    nutritionFacts: '',
    nutritionValues: nutritionSettings
      .filter(value => value.ask_by_default)
      .map(value => ({ id: value.id, value: undefined })),
    posId: '',
    price: undefined,
    producerInfo: '',
    productHierarchyLevelId: undefined,
    sourceCountry: undefined,
    sourceConfig: undefined,
    usageInstructions: '',
    userRequirement: undefined,
    useWeeklyAvailability: false,
    useWeeklyVisibility: false,
    vat,
    weeklyAvailability: [],
    weeklyVisibility: [],
    numberOfUnits: undefined,
    volumeInMl: undefined,
    weightInGrams: undefined,
    displayUnit: null,
    maxQuantityPerPurchase: undefined,
    minQuantityPerPurchase: undefined,
    location: itemConfig?.storageLocation || undefined,
    pickingAreaId,
    pickingItemSequence: undefined,
    weightType: 'pre-packaged',
    sellByWeightInputType: undefined,
    gramsPerStep: undefined,
    optionBindings: [],
    externalInfoHyperlink: undefined,
    isWoltPlusOnly: false,
    returnPolicy: null,
  };
};

export const checkIfItemIsAvailable = (
  now: number,
  availabilityPeriod: { start: MongoDate | null; end: MongoDate | null },
) => {
  if (
    (availabilityPeriod.start && availabilityPeriod.start.$date > now) ||
    (availabilityPeriod.end && availabilityPeriod.end.$date <= now)
  ) {
    return false;
  }

  return true;
};

export const cleanUpInvalidPrerequisiteOptionChoices = (
  newOptionBindings: OptionBinding[],
  oldOptionBindings: OptionBinding[],
  options: Option[],
) => {
  const removedOptionBindings = oldOptionBindings.filter(ob => !newOptionBindings.some(newOb => newOb.id === ob.id));
  const removedOptionChoiceIds = removedOptionBindings
    .flatMap(ob => options.find(option => option.id === ob.option_id)?.values.map(choice => choice.id))
    .filter(isDefined);

  return newOptionBindings.map(ob => ({
    ...ob,
    prerequisite_values: ob.prerequisite_values.filter(
      prereqChoiceId => !removedOptionChoiceIds.includes(prereqChoiceId),
    ),
  }));
};
